import { defineStore } from "pinia";
import { ref, computed } from "vue";
import {
  signInWithPopup,
  GoogleAuthProvider,
  signOut as fbSignOut,
  onAuthStateChanged,
  type User,
} from "firebase/auth";
import { doc, onSnapshot, updateDoc, type Unsubscribe } from "firebase/firestore";
import { auth, googleProvider, db } from "../firebase";

export interface UserProfile {
  email: string;
  displayName: string;
  photoURL: string;
  plan: "free" | "pro";
  locale?: "fr" | "en";
  quotesCount: number;
  quotesThisMonth: number;
  currentMonth: string;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  subscriptionStatus: string | null;
  settings: Record<string, string | Record<string, string>>;
  /**
   * Set when user clicks "Delete my account" in Settings. Triggers
   * the 30-day grace period — if the user logs back in, they see
   * the cancel-deletion modal. Once `now() - deletedAt > 30 days`,
   * the purgeDeletedAccounts cron hard-deletes everything.
   *
   * Modifiable UNIQUEMENT via Cloud Functions (admin SDK) — the
   * Firestore rules do not list `deletedAt` in the affectedKeys
   * whitelist for client updates, so users cannot self-set or
   * self-clear it. v1.12.53+
   */
  deletedAt?: { seconds: number; nanoseconds: number } | null;
  /**
   * Per-user feature flags. Each key is a major feature name ; the
   * value is a boolean (true = enabled, false = disabled).
   * Modifiable UNIQUEMENT via Firebase Console (admin SDK), pas par
   * le user lui-même — les Firestore rules bloquent l'écriture sur
   * ce champ. Voir FEATURE_FLAGS.md.
   *
   * Si absent ou flag manquant → default OFF (le user n'a pas accès
   * à la feature tant que l'admin l'active explicitement).
   */
  featureFlags?: {
    gpm?: boolean;
    cieca?: boolean;
  };
}

export const useAuthStore = defineStore("auth", () => {
  const user = ref<User | null>(null);
  const profile = ref<UserProfile | null>(null);
  const loading = ref(true);
  let profileUnsub: Unsubscribe | null = null;

  const isPro = computed(() => profile.value?.plan === "pro");
  const isSignedIn = computed(() => !!user.value);

  /**
   * True when the user has clicked "Delete my account" and is in
   * the 30-day grace period. The login flow shows a blocking modal
   * to give them a chance to cancel before the cron purges them.
   * v1.12.53+
   */
  const isAccountDeletionPending = computed(() => {
    return profile.value?.deletedAt != null;
  });

  /**
   * Effective deletion date (deletedAt + 30 days), as a Date.
   * Returns null if not pending. Used by UI to show "expires on X".
   */
  const accountDeletionEffectiveDate = computed<Date | null>(() => {
    const da = profile.value?.deletedAt;
    if (!da) return null;
    const seconds = (da as { seconds: number }).seconds;
    if (typeof seconds !== "number") return null;
    // 30 days in milliseconds
    return new Date(seconds * 1000 + 30 * 24 * 60 * 60 * 1000);
  });

  function init() {
    onAuthStateChanged(auth, (u) => {
      user.value = u;
      if (profileUnsub) { profileUnsub(); profileUnsub = null; }

      if (u) {
        // Flag pour ne wiper l'adresse legacy qu'une fois par login.
        // Le snapshot listener déclenche à chaque update du profil
        // (plans Stripe, settings, etc.) — on ne veut pas re-wiper
        // à chaque tick.
        let legacyAddressChecked = false;

        // Subscribe to profile doc — auto-updates on plan changes from Stripe webhooks
        profileUnsub = onSnapshot(doc(db, "users", u.uid), (snap) => {
          profile.value = snap.exists() ? (snap.data() as UserProfile) : null;
          loading.value = false;

          // ─────────────────────────────────────────────────────────
          // Clear silencieux de l'adresse compagnie legacy (v1.12.51+)
          // ─────────────────────────────────────────────────────────
          // Avant v1.12.51, l'adresse compagnie pouvait être stockée
          // comme une string libre (genre "18 av X, G4Z 1T4, ville,
          // qc, ca") sans `addressComponents`. Le rendu PDF/image
          // splittait alors naïvement sur les virgules → 5 lignes
          // illisibles au lieu du format Postes Canada.
          //
          // Fix : si `address` existe sans `addressComponents`, on
          // clear `address`. Le user voit un champ vide à sa
          // prochaine ouverture de Settings et re-sélectionne via
          // l'autocomplete Google Places (ce qui remplit
          // `addressComponents` correctement).
          //
          // Idempotent : une fois `address` clearée, le check
          // suivant trouve `!address` et ne fait rien. Le wipe ne
          // s'exécute donc jamais 2 fois pour le même compte.
          if (legacyAddressChecked) return;
          legacyAddressChecked = true;

          const settings = profile.value?.settings as
            | { address?: string; addressComponents?: unknown }
            | undefined;
          if (!settings) return;

          const hasLegacyAddress =
            typeof settings.address === "string" &&
            settings.address.trim() !== "";
          const hasComponents =
            settings.addressComponents != null &&
            typeof settings.addressComponents === "object";

          if (hasLegacyAddress && !hasComponents) {
            // Update Firestore — clear seulement le champ address.
            // Le reste de settings est préservé. updateDoc avec
            // dot-notation pour ne toucher qu'un sous-champ.
            updateDoc(doc(db, "users", u.uid), {
              "settings.address": "",
              updatedAt: new Date().toISOString(),
            }).catch((e) => {
              // Best-effort. Si l'update échoue (réseau, rules), on
              // log et on continue — pas de blocage du login.
              console.warn("[auth] legacy address wipe failed:", e);
            });
          }
        });
      } else {
        profile.value = null;
        loading.value = false;
      }
    });
  }

  async function signInWithGoogle() {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      // Capture the Google OAuth access token so we can call People API later
      const credential = GoogleAuthProvider.credentialFromResult(result);
      console.log("[auth] Google sign-in result:", {
        hasCredential: !!credential,
        hasAccessToken: !!credential?.accessToken,
      });
      if (credential?.accessToken) {
        sessionStorage.setItem("google_access_token", credential.accessToken);
        sessionStorage.setItem("google_token_issued_at", Date.now().toString());
      } else {
        console.warn("[auth] No accessToken from signInWithPopup — will try GIS fallback on-demand");
      }
    } catch (e: any) {
      console.error("[auth] signInWithPopup error:", e);
      throw e;
    }
  }

  /**
   * Request Google contacts access token via Google Identity Services.
   * Use this as a fallback when signInWithPopup doesn't return the accessToken.
   * Returns a Promise<string> with the token, or rejects on failure.
   */
  async function requestGoogleContactsToken(): Promise<string> {
    // Load GIS script if not loaded yet
    if (!(window as any).google?.accounts?.oauth2) {
      await new Promise<void>((resolve, reject) => {
        const existing = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
        if (existing) { existing.addEventListener("load", () => resolve()); return; }
        const script = document.createElement("script");
        script.src = "https://accounts.google.com/gsi/client";
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error("Failed to load Google Identity Services"));
        document.head.appendChild(script);
      });
    }

    // The OAuth client ID tied to the Firebase project's web app
    // Set via VITE_GOOGLE_OAUTH_CLIENT_ID in .env.production
    // (Find it at: https://console.cloud.google.com/apis/credentials)
    const CLIENT_ID = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID
      || "563584335869-fgrhgmd47bqnekij5i8b5pr03ho849e6.apps.googleusercontent.com";

    return new Promise<string>((resolve, reject) => {
      const tokenClient = (window as any).google.accounts.oauth2.initTokenClient({
        client_id: CLIENT_ID,
        scope: "https://www.googleapis.com/auth/contacts.readonly",
        prompt: "consent",
        callback: (response: any) => {
          if (response.error) { reject(new Error(response.error)); return; }
          if (response.access_token) {
            sessionStorage.setItem("google_access_token", response.access_token);
            sessionStorage.setItem("google_token_issued_at", Date.now().toString());
            resolve(response.access_token);
          } else {
            reject(new Error("No access_token in GIS response"));
          }
        },
        error_callback: (err: any) => reject(new Error(err.message || "GIS error")),
      });
      tokenClient.requestAccessToken();
    });
  }

  async function signOut() {
    sessionStorage.removeItem("google_access_token");
    sessionStorage.removeItem("google_token_issued_at");

    // v1.13.0 — Reset des stores per-user pour éviter que le prochain
    // utilisateur connecté voie les données du précédent. Import dynamique
    // pour éviter les cycles de dépendances (ces stores utilisent auth).
    try {
      const { useReclamationsConfigStore } = await import("./reclamationsConfig");
      const { useClientsStore } = await import("./clients");
      const { useReclamationsStore } = await import("./reclamations");
      const { useFichiersStore } = await import("./fichiers");
      useReclamationsConfigStore().reset();
      useClientsStore().reset();
      useReclamationsStore().reset();
      useFichiersStore().reset();
    } catch (e) {
      // Non-blocking : si le reset échoue on continue le signOut normalement
      console.warn("[auth.signOut] Reset des stores a échoué:", e);
    }

    await fbSignOut(auth);
  }

  function getGoogleAccessToken(): string | null {
    const token = sessionStorage.getItem("google_access_token");
    const issuedAt = parseInt(sessionStorage.getItem("google_token_issued_at") || "0", 10);
    // OAuth tokens typically expire in 1 hour; consider them stale after 55 min
    if (!token || Date.now() - issuedAt > 55 * 60 * 1000) return null;
    return token;
  }

  return {
    user, profile, loading, isPro, isSignedIn,
    isAccountDeletionPending, accountDeletionEffectiveDate,
    init, signInWithGoogle, signOut, getGoogleAccessToken, requestGoogleContactsToken,
  };
});
